import { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom'
import { useIntl } from 'react-intl';
import moment from 'moment';

//Components
import { Modal, ModalBody, ModalFooter, Button, Select, Form, ToggleSwitch, ProgressBar } from "@gpstrackit/react-controls";
import { NotificationManager } from 'Components/Notifications';
import { DatePicker } from "@gpstrackit/gtc-react-controls";
import ChipStatus from 'Components/ChipStatus';
import CustomCard from 'Components/Card';
import { Chip, Divider, Alert } from '@mui/material';
import { getStatusOptions, copyToClipboard } from "Core/data/Helpers"
//Actions
import {
    changeStatusSearchScheduledActionsRequest,
    changeStatusCreateScheduledActionRequest,
    scheduledActionsOpenModal,
    changeStatusUpdateRequest,
    changeStatusSetDevices,
    clearRedux,
} from 'Redux/actions';

//Views
import DeleteScheduledAction from 'Modules/scheduledActions/views/components/deleteScheduledAction';

//Helpers
import { HasPermission } from "Core/security"

//Styles
import './index.scss';

const ChangeStatus = (props) => {
    const { tableRef, single, getDevice, companyId } = props;
    const dispatch = useDispatch();
    const { messages } = useIntl();
    let now = moment.utc();
    const navigate = useNavigate()
    const formRef = useRef(null);

    let statusLabels = [
        {
            id: 1,
            label: "Active"
        },
        {
            id: 2,
            label: "Voluntary Suspension"
        },
        {
            id: 3,
            label: "Involuntary Suspesion"
        },
        {
            id: 4,
            label: "Cancelled"
        }
    ]

    //Redux states
    const { loading, openModal, result, devices, schedule, scheduledActions } = useSelector(state => state.changeStatusRedux);
    const deleteScheduledActionOpenModal = useSelector(state => state.deleteScheduledActionRedux.openModal);
    const deleteScheduledActionResult = useSelector(state => state.deleteScheduledActionRedux.result);
    const user = useSelector(state => state.securityRedux.user);

    //Local States
    const [scheduled, setScheduled] = useState(false);
    const [date, setDate] = useState(now.add(12, 'hours'));
    const [step, setStep] = useState(1);
    const [status, setStatus] = useState(null);
    const [showWeb, setShowWeb] = useState(true);
    const [additionalChanges, setAdditionalChanges] = useState(false);
    const [additionalDate, setAdditionalDate] = useState(null);
    const [errorAdditionalStatus, setErrorAdditionalStatus] = useState(null);
    const [errorAdditionalDate, setErrorAdditionalDate] = useState(null);
    const [additionalStatus, setAdditionalStatus] = useState(null);
    const [additionalShowWeb, setAdditionalShowWeb] = useState(true);

    const closeModal = () => {
        dispatch(dispatch(clearRedux("CHANGE_STATUS")));
        setStep(1);
        setStatus(null);
        setShowWeb(true);
        setScheduled(false);
        setDate(now.add(12, 'hours'))
    }

    const applyChanges = () => {

        let entities = devices.map((item) => ({ entityId: item?.id, companyId: item?.companyId }))
        let unitIds = devices.map((item) => (item.id))

        if (!scheduled) {
            let payload = {
                unitIds: unitIds,
                features: {
                    show: showWeb
                },
                status: status?.id,
            }
            let currentScheduledActions = scheduledActions ? scheduledActions.map((item) => ({ ...item, format: JSON.parse(item.data) })) : null
            if (currentScheduledActions && currentScheduledActions.length) {
                let validate = currentScheduledActions.some((item) => item?.format?.newStatusId === status?.id)
                if (validate) {
                    NotificationManager.error(
                        messages['change_status_error_status_by_date'],
                        messages['change_status_title'],
                        3000
                    );
                    return
                }
            }
            dispatch(changeStatusUpdateRequest(payload));
            if (additionalChanges) {
                let newAdditionalStatus = statusLabels.find((item) => item.id === additionalStatus?.id)
                let payload = {
                    entityName: "Unit",
                    entities: entities,
                    scheduledActions: [{
                        data: JSON.stringify({ newStatusId: additionalStatus?.id, show: additionalShowWeb, newStatusName: newAdditionalStatus.label }),
                        scheduleOn: moment(additionalDate).format('YYYY-MM-DD')
                    }]
                }
                dispatch(changeStatusCreateScheduledActionRequest(payload))
            }
        } else {
            let newStatus = statusLabels.find((item) => item.id === status?.id)
            let scheduledActions = [{
                data: JSON.stringify({ newStatusId: status?.id, show: showWeb, newStatusName: newStatus.label }),
                scheduleOn: moment(date).format('YYYY-MM-DD')
            }]
            if (additionalChanges) {
                let newAdditionalStatus = statusLabels.find((item) => item.id === additionalStatus?.id)
                scheduledActions.push({
                    data: JSON.stringify({ newStatusId: additionalStatus?.id, show: additionalShowWeb, newStatusName: newAdditionalStatus.label }),
                    scheduleOn: moment(additionalDate).format('YYYY-MM-DD')
                })
            }
            let payload = {
                entityName: "Unit",
                entities: entities,
                scheduledActions: scheduledActions
            }
            dispatch(changeStatusCreateScheduledActionRequest(payload))
        }
    }

    useEffect(() => {
        if (result) {
            if (result?.update) {
                setStep(1);
                setStatus(null);
                closeModal();

                let transactionId = result?.transactionId;
                let message = messages["change_status_transaction_message"]
                NotificationManager.success(message, messages['change_status_title'], 10000, () => {
                    copyToClipboard(transactionId, messages['common_copied']);
                    navigate(`/app/transactions/list/${transactionId}`);

                }, null, '', null);

                tableRef && tableRef?.current?.getData();
                getDevice && getDevice();
            } else {
                NotificationManager.error(
                    messages['change_status_it_was_possible_to_change_the_status'],
                    messages['change_status_title'],
                    3000
                );
            }
            dispatch(dispatch(clearRedux("CHANGE_STATUS", { result: null })));
        }
    }, [result])

    useEffect(() => {
        if (schedule && scheduled) {
            if (!schedule?.error) {
                NotificationManager.success(
                    messages['account_change_status_programmed'],
                    messages['change_status_title'],
                    3000,
                );
                closeModal();
            } else if (schedule?.error) {
                NotificationManager.error(
                    schedule?.error.includes("Entities") ? messages['change_status_error_scheduled_action'] : messages['change_status_error_scheduled'],
                    messages['change_status_title'],
                    3000,
                );
                dispatch(dispatch(clearRedux("CHANGE_STATUS", { schedule: null })));
            }
        }
    }, [schedule])

    useEffect(() => {
        if (openModal && single) setStep(2)
    }, [openModal])

    useEffect(() => {
        if (openModal && devices && devices.length) {
            let deviceIds = devices.map((item) => (item.id))
            dispatch(changeStatusSearchScheduledActionsRequest(deviceIds))
        }
    }, [devices, openModal])

    useEffect(() => {
        if (deleteScheduledActionResult && deleteScheduledActionResult.delete && devices && devices.length) {
            let deviceIds = devices.map((item) => (item.id))
            dispatch(changeStatusSearchScheduledActionsRequest(deviceIds))
        }
    }, [deleteScheduledActionResult])

    let currentStatus = status
    let stateValidation = devices.length > 0 ? devices.some((item) => item?.status === currentStatus?.id) : false;

    return (
        <>
            <Modal
                open={openModal}
                title={messages['change_status_title']}
                handleClose={() => closeModal()}
                position="right"
                size="small"
                loading={loading}
            >
                <ModalBody>
                    <ProgressBar steps={3} activeStep={step} />
                    {
                        step === 1 &&
                        <div style={{ padding: '0 10px', marginBottom: 30 }}>
                            {
                                devices.map((item, index) => {
                                    return (
                                        <CustomCard
                                            key={index}
                                            onClick={() => {
                                                let currentDevices = devices;
                                                let selected = currentDevices.findIndex((device) => device.id === item.id);
                                                if (selected > -1) currentDevices.splice(selected, 1);
                                                if (currentDevices.length === 0) closeModal();
                                                dispatch(changeStatusSetDevices(currentDevices));
                                            }}
                                        >
                                            <div style={{ display: 'grid' }}>
                                                <div>
                                                    <label style={{ fontWeight: 700, marginRight: 5 }}>{messages['change_status_device_name']}</label><span>{item?.label}</span>
                                                </div>
                                                <div>
                                                    <label style={{ fontWeight: 700, marginRight: 5 }}>ESN</label><span>{item?.esn}</span>
                                                </div>
                                                <div><ChipStatus statusCode={item?.status} messages={messages} width={150} showTooltip /></div>
                                            </div>
                                        </CustomCard>
                                    )
                                })
                            }
                        </div>
                    }
                    {
                        step === 2 &&
                        <div style={{ padding: '0 10px', marginBottom: 32 }}>
                            <Form
                                ref={formRef}
                                onValidSubmit={() => applyChanges()}
                                onSubmit={() => {
                                    if (additionalChanges && status && additionalStatus) {
                                        if (status.id === additionalStatus.id) {
                                            setErrorAdditionalStatus('change_status_error_status');
                                            return false
                                        }
                                    }
                                    if (additionalChanges && !additionalDate) {
                                        setErrorAdditionalDate('required_field');
                                        return false
                                    }
                                    if (scheduled && additionalChanges && additionalDate && date) {
                                        let firstDate = moment(date).format('YYYY-MM-DD');
                                        let secondDate = moment(additionalDate).format('YYYY-MM-DD');
                                        if (secondDate <= firstDate) {
                                            setErrorAdditionalDate('change_status_error_date');
                                            return false
                                        }
                                    }

                                    let dateValidation = scheduled && date ? scheduledActions.filter((item) => moment(item.scheduleOn).utc().format('YYYY-MM-DD') === moment(date).format('YYYY-MM-DD')) : []
                                    let additionalDateValidation = additionalChanges && additionalDate ? scheduledActions.filter((item) => moment(item.scheduleOn).utc().format('YYYY-MM-DD') === moment(additionalDate).format('YYYY-MM-DD')) : []

                                    let currentStatus = []

                                    try {
                                        currentStatus = scheduledActions.map((item) => ({ ...item, params: item?.data ? JSON.parse(item.data) : null }))
                                    } catch (exc) { }

                                    let validateStatus = scheduled && status ? currentStatus?.filter((item) => item?.params?.newStatusId === status?.id) : []
                                    let validateAdditionalStatus = additionalChanges && additionalStatus ? currentStatus?.filter((item) => item?.params?.newStatusId === additionalStatus?.id) : []

                                    if ((scheduled && validateStatus?.length) || (additionalChanges && validateAdditionalStatus?.length)) {
                                        NotificationManager.warning(
                                            messages['change_status_error_same_status'],
                                            messages['change_status_title'],
                                            3000,
                                        );
                                        return false
                                    }

                                    if ((scheduled && dateValidation?.length) || (additionalChanges && additionalDateValidation?.length)) {
                                        NotificationManager.warning(
                                            messages['change_status_error_same_date'],
                                            messages['change_status_title'],
                                            3000,
                                        );
                                        return false
                                    }


                                    setErrorAdditionalStatus(null)
                                    setErrorAdditionalDate(null)
                                    return true
                                }}
                            >
                                <Select
                                    label={messages['change_status_status']}
                                    placeholder={messages['change_status_status']}
                                    id="change-status-list"
                                    options={getStatusOptions(messages).filter((item) => item.label !== "RMA")}
                                    onChange={(value) => {
                                        setStatus(value)
                                        setAdditionalStatus(null)
                                    }}
                                    value={status}
                                    valueField="id"
                                    textField="label"
                                    required
                                    requiredMessage={messages['required_field']}
                                    className={stateValidation && "device-change-status-error-status"}
                                />
                                {stateValidation && <small className="select-error text-danger">{messages['change_status_warning_status']}</small>}
                                <div style={{ marginTop: 5, width: '100%', alignItems: 'center' }}>
                                    <label style={{ marginRight: 7, marginTop: 1 }} className="input-normal-label">{messages['feature_tab_show_in_web']} </label>
                                    <ToggleSwitch
                                        id="change-status-show-web"
                                        blueToggle
                                        large
                                        optionLabels={[messages['create_account_yes'], messages["create_account_not"]]}
                                        checked={showWeb}
                                        onChange={(checked) => setShowWeb(checked)}
                                        width={100}
                                    />
                                </div>
                                {
                                    HasPermission(user, "schact", "c") ? (
                                        <>
                                            <div style={{ marginTop: 5, width: '100%', display: 'flex', alignItems: 'center' }}>
                                                <div>
                                                    <label style={{ marginRight: 7, marginTop: 1 }} className="input-normal-label">{messages['change_status_apply_status']}</label>
                                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                                        <Chip
                                                            onClick={(() => setScheduled(false))}
                                                            style={{
                                                                marginRight: 10,
                                                                cursor: 'pointer',
                                                                backgroundColor: !scheduled ? '#1D2734' : "#00000014",
                                                                color: !scheduled ? 'white' : "#000000de"
                                                            }}
                                                            label={messages['change_status_immediately']}
                                                        />
                                                        <Chip
                                                            onClick={(() => setScheduled(true))}
                                                            style={{
                                                                marginRight: 5,
                                                                cursor: 'pointer',
                                                                backgroundColor: scheduled ? '#1D2734' : "#00000014",
                                                                color: scheduled ? 'white' : "#000000de"
                                                            }}
                                                            label={messages['change_status_schedule']}
                                                        />
                                                    </div>

                                                </div>
                                            </div>
                                            {
                                                scheduled ? (
                                                    <div style={{ marginTop: 10 }}>
                                                        <label className="input-normal-label" style={{ marginRight: 5 }}>{messages['change_status_scheduled_at']}<span className="text-danger"> *</span></label>
                                                        <DatePicker
                                                            calendarPosition="bottom"
                                                            format="MMMM DD, yyyy"
                                                            minDate={now.add(12, 'hours')}
                                                            onChange={(date) => setDate(date)}
                                                            placeholder={messages['feature_tab_select_date']}
                                                            showButtons
                                                            style={{ height: '42px' }}
                                                            timeZone={0}
                                                            value={date}
                                                        />
                                                    </div>
                                                ) : <></>
                                            }
                                            <Divider style={{ marginTop: 15 }} />
                                            <div style={{ marginTop: 12, width: '100%', alignItems: 'center' }}>
                                                <label style={{ marginRight: 7, marginTop: 1 }} className="input-normal-label">{messages['change_status_additional_change']}</label>
                                                <ToggleSwitch
                                                    id="change-additional-changes"
                                                    blueToggle
                                                    large
                                                    optionLabels={[messages['create_account_yes'], messages["create_account_not"]]}
                                                    checked={additionalChanges}
                                                    onChange={(checked) => setAdditionalChanges(checked)}
                                                    width={100}
                                                />
                                            </div>
                                            {
                                                additionalChanges ? (
                                                    <>
                                                        <div style={{ marginTop: 5 }}>
                                                            <Select
                                                                label={messages['change_status_status']}
                                                                placeholder={messages['change_status_status']}
                                                                id="change-additional-additional-status-list"
                                                                options={getStatusOptions(messages).filter((item) => item.label !== "RMA" && item.id !== status?.id)}
                                                                onChange={(value) => setAdditionalStatus(value)}
                                                                value={additionalStatus}
                                                                valueField="id"
                                                                textField="label"
                                                                required
                                                                requiredMessage={messages['required_field']}
                                                                className={errorAdditionalStatus && "device-change-status-error-status"}
                                                            />
                                                            {errorAdditionalStatus && <small className="select-error text-danger">{messages[errorAdditionalStatus]}</small>}
                                                        </div>
                                                        <div style={{ marginTop: 5, width: '100%', alignItems: 'center' }}>
                                                            <label style={{ marginRight: 7, marginTop: 1 }} className="input-normal-label">{messages['feature_tab_show_in_web']} </label>
                                                            <ToggleSwitch
                                                                id="change-status-additional-show-web"
                                                                blueToggle
                                                                large
                                                                optionLabels={[messages['create_account_yes'], messages["create_account_not"]]}
                                                                checked={additionalShowWeb}
                                                                onChange={(checked) => setAdditionalShowWeb(checked)}
                                                                width={100}
                                                            />
                                                        </div>
                                                        <div style={{ marginTop: 10 }}>
                                                            <label className="input-normal-label" style={{ marginRight: 5 }}>{messages['change_status_scheduled_at']}<span className="text-danger"> *</span></label>
                                                            <DatePicker
                                                                calendarPosition="bottom"
                                                                format="MMMM DD, yyyy"
                                                                minDate={date || now.add(12, 'hours')}
                                                                onChange={(date) => {
                                                                    setErrorAdditionalDate(null);
                                                                    setAdditionalDate(date);
                                                                }}
                                                                placeholder={messages['feature_tab_select_date']}
                                                                showButtons
                                                                style={{ height: '42px' }}
                                                                timeZone={0}
                                                                value={additionalDate}
                                                                className={errorAdditionalDate && "device-change-status-error-date"}
                                                            />
                                                            {errorAdditionalDate && <small className="select-error text-danger">{messages[errorAdditionalDate]}</small>}
                                                        </div>
                                                    </>
                                                ) : <></>
                                            }
                                            {
                                                scheduledActions?.length && (scheduled || additionalChanges) ?
                                                    <div style={{ marginTop: 15 }}>
                                                        <Divider style={{ marginBottom: 15 }} />
                                                        <Alert severity="warning" style={{ marginBottom: 10 }}>{devices && devices?.length === 1 ? messages['change_status_device_already_scheduled_actions'] : messages['change_status_devices_already_scheduled_actions']}</Alert>
                                                        {
                                                            scheduledActions.map((item, index) => {
                                                                let device = devices.find((device) => device?.id === item?.entityId)
                                                                let status = null
                                                                let showInWeb = null
                                                                try {
                                                                    let data = item.data
                                                                    data = JSON.parse(data)
                                                                    if (data?.newStatusId) {
                                                                        showInWeb = data?.show
                                                                        status = getStatusOptions(messages).find((status) => status.id === data?.newStatusId)
                                                                    }
                                                                } catch (exc) { }

                                                                return (
                                                                    <CustomCard
                                                                        key={index}
                                                                        onClick={() => dispatch(scheduledActionsOpenModal([item?.id]))}
                                                                    >
                                                                        <div style={{ display: 'grid' }}>
                                                                            <div>
                                                                                <label style={{ fontWeight: 700, marginRight: 5 }}>{messages['change_status_serial']}: </label><span>{device?.esn}</span>
                                                                            </div>
                                                                            <div>
                                                                                <label style={{ fontWeight: 700, marginRight: 5 }}>{messages['change_status_new_status']}: </label><span>{status.label}</span>
                                                                            </div>
                                                                            <div>
                                                                                <label style={{ fontWeight: 700, marginRight: 5 }}>{messages['change_status_show_in_web']}: </label><span>{showInWeb ? messages['create_account_yes'] : messages['create_account_not']}</span>
                                                                            </div>
                                                                            <div>
                                                                                <label style={{ fontWeight: 700, marginRight: 5 }}>{messages['change_status_scheduled_on']}: </label><span>{item?.scheduleOn ? moment(item?.scheduleOn).utc().format('MMM DD YYYY') : ""}</span>
                                                                            </div>
                                                                        </div>
                                                                    </CustomCard>
                                                                )
                                                            })
                                                        }
                                                    </div> : <></>
                                            }
                                        </>
                                    ) : <></>
                                }
                            </Form>
                        </div>
                    }
                </ModalBody>
                <ModalFooter>
                    <Button
                        className={"btn-cancel"}
                        onClick={() => closeModal()}
                    >
                        {messages['cancel']}
                    </Button>
                    {
                        step === 2 && !single &&
                        <Button
                            style={{ backgroundColor: '#e4e4e4' }}
                            className="btn-cancel"
                            onClick={() => {
                                setStatus(null);
                                setStep(1);
                            }}
                        >
                            {messages['common_back']}
                        </Button>
                    }
                    <Button
                        disabled={stateValidation}
                        className="btn-dark-blue"
                        onClick={() => step === 2 ? formRef?.current?.submit() : setStep(2)}
                    >
                        {step === 2 ? messages['change_status_change'] : messages['common_next']}
                    </Button>
                </ModalFooter>
            </Modal>
            {deleteScheduledActionOpenModal && <DeleteScheduledAction />}
        </>
    )
}

export default ChangeStatus;